import { Suspense } from "react";
import { Outlet, useLocation, matchPath, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

function CartHeader() {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith("pt") ? "pt" : "en";
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background">
      <div className="container flex h-14 items-center justify-between lg:h-16">
        <Link to="/" className="flex min-h-11 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <TicketHallLogo size="md" />
        </Link>
        <Button variant="ghost" className="h-11 min-w-11 gap-2 rounded-xl px-3 text-xs text-muted-foreground" onClick={() => i18n.changeLanguage(language === "pt" ? "en" : "pt")} aria-label={language === "pt" ? "Mudar idioma para inglês" : "Change language to Portuguese"}>
          <Globe aria-hidden="true" className="h-4 w-4" />
          {language.toUpperCase()}
        </Button>
      </div>
    </header>
  );
}

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[70vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

export function MainLayout() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const { pathname } = useLocation();
  const compactFooter = ["/eventos/:slug", "/organizacao/:username/eventos/:slug", "/produtor/:username/eventos/:slug"].some(path => matchPath(path, pathname));
  const hideFooter = pathname.startsWith("/meu-perfil") || pathname === "/carrinho";

  // Bottom nav only shows for logged-in users on mobile
  const hasBottomNav = user && isMobile;

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      {pathname === "/carrinho" ? <CartHeader /> : <Navbar />}
      <main className={cn(
        "overflow-x-clip",
        "pt-14 lg:pt-16",
        hasBottomNav ? "pb-20 lg:pb-0" : "",
        pathname === "/carrinho" && (hasBottomNav ? "pb-56 lg:pb-0" : "pb-36 lg:pb-0")
      )}>
        <Suspense fallback={<PageFallback />}>
          <Outlet />
          {!hideFooter && <Footer compact={compactFooter} />}
        </Suspense>
      </main>
    </div>
  );
}
